import { useCallback, useEffect, useRef, useState } from "react";
import type { Profile } from "@shyne/shared/api";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import MainLayout from "@/components/layouts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { useAccountSettings } from "./account.hooks";
import { isPhotoFile, PHOTO_FILE_TYPES, UNSUPPORTED_PHOTO_FILE } from "@/lib/appointment-photos";
import { optionalWebAddress } from "@/lib/web-address";

const formSchema = z.object({
  image: z.union([z.instanceof(File), z.string()]).optional(),
  name: z.string().trim().min(1, "Full name is required"),
  email: z.string(),
  phone: z.string().optional(),
  description: z.string().optional(),
  location: z.string().optional(),
  twitter: optionalWebAddress,
  linkedin: optionalWebAddress,
});
type FormValues = z.infer<typeof formSchema>;

const formValuesOf = (user: Profile): FormValues => ({
  image: user.avatar ?? "",
  name: user.name,
  email: user.email,
  phone: user.phone ?? "",
  description: user.description ?? "",
  location: user.location ?? "",
  twitter: user.twitter ?? "",
  linkedin: user.linkedin ?? "",
});

const BLANK_PROFILE: FormValues = { image: "", name: "", email: "", phone: "", description: "", location: "", twitter: "", linkedin: "" };

export default function AccountPage() {
  const { user, loading, saving, uploadingAvatar, handleSave, handleAvatarUpload } = useAccountSettings();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: BLANK_PROFILE,
  });

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarRefusal, setAvatarRefusal] = useState<string | null>(null);
  const submitting = useRef(false);

  const showSaved = useCallback(
    (saved: Profile) => {
      form.reset(formValuesOf(saved));
      setAvatarPreview(saved.avatar ?? null);
    },
    [form]
  );

  useEffect(() => {
    if (user && !submitting.current) showSaved(user);
  }, [user, showSaved]);

  const fileToRHF = (file: File) => {
    if (!isPhotoFile(file)) {
      setAvatarRefusal(UNSUPPORTED_PHOTO_FILE);
      return;
    }
    setAvatarRefusal(null);
    form.setValue("image", file as unknown as FormValues["image"]);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const onSubmit = form.handleSubmit(async (values) => {
    submitting.current = true;
    try {
      const saved = await handleSave({
        name: values.name,
        phone: values.phone ?? null,
        description: values.description ?? null,
        location: values.location ?? null,
        twitter: values.twitter ?? null,
        linkedin: values.linkedin ?? null,
      });
      showSaved(values.image instanceof File ? await handleAvatarUpload(values.image) : saved);

      toast.success("Profile updated successfully.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update profile.");
    } finally {
      submitting.current = false;
    }
  });

  const fields: FormFieldDef[] = [
    {
      type: "file",
      name: "image",
      label: "",
      wrapperClassName: "md:col-span-2 flex items-center space-x-6 !p-0 !border-0",
      hidden: true,
    },

    { type: "text", name: "name", label: "Full Name", required: true },
    { type: "email", name: "email", label: "Email", required: true, disabled: true },
    { type: "text", name: "phone", label: "Phone" },
    { type: "text", name: "location", label: "Location" },

    {
      type: "textarea",
      name: "description",
      label: "About / Bio",
      wrapperClassName: "md:col-span-2",
    },

    { type: "text", name: "twitter", label: "Twitter" },
    { type: "text", name: "linkedin", label: "LinkedIn" },
  ];

  const breadcrumb = [
    { name: "Settings", href: "#" },
    { name: "Account", href: "#" },
  ];

  if (loading || !user) {
    return (
      <MainLayout
        breadcrumb={breadcrumb}
        pageHeader={{
          title: "Edit Profile",
          subtitle: "Update your account details.",
        }}
      >
        <p>Loading…</p>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={breadcrumb}
      pageHeader={{
        title: "Edit Profile",
        subtitle: "Update your account details.",
      }}
    >
      <div className="container mx-auto">
        <div className="flex items-center space-x-6 mb-6">
          <Avatar className="w-24 h-24 rounded-full border border-gray-300">
            {avatarPreview ? (
              <AvatarImage src={avatarPreview} alt="User Avatar" className="object-cover object-top" />
            ) : (
              <AvatarFallback>
                {user.name
                  .split(" ")
                  .map((w) => w[0])
                  .join("")}
              </AvatarFallback>
            )}
          </Avatar>

          <div className="flex flex-col gap-3">
            <Label
              htmlFor="avatarUpload"
              className="cursor-pointer flex items-center gap-2 text-primary hover:underline pointer-events-auto"
            >
              <Upload className="w-5 h-5" />
              Change Avatar
            </Label>

            <input
              id="avatarUpload"
              type="file"
              accept={PHOTO_FILE_TYPES}
              className="hidden"
              onChange={(e) => e.target.files?.[0] && fileToRHF(e.target.files[0])}
            />

            <p className="text-sm text-muted-foreground">
              JPG, PNG up to 2&nbsp;MB.
              {uploadingAvatar && <>&nbsp;Uploading…</>}
            </p>
            {avatarRefusal && (
              <p role="alert" className="text-sm text-destructive">
                {avatarRefusal}
              </p>
            )}
          </div>
        </div>

        <FormCreator
          form={form}
          onSubmit={onSubmit}
          formDefinition={fields}
          buttonText={saving ? "Saving…" : "Update Profile"}
          formClassName="grid grid-cols-1 md:grid-cols-2 gap-6"
        />
      </div>
    </MainLayout>
  );
}
